import { supabase } from '@/integrations/supabase/client';
import { inheritedMappingRowVisible } from '@/shared/variantMapping/cloneMappingCutoff';
import { toMatchKey } from '@/shared/variantMapping/matchKey';

// product_identifier_links is not in the generated types, so it is reached the
// same way the rest of the app reaches it.
const supabaseAny = supabase as unknown as {
  from: (table: string) => {
    select: (cols: string) => {
      eq: (col: string, val: string) => {
        maybeSingle: () => Promise<{ data: unknown; error: unknown }>;
        order: (col: string, opts: { ascending: boolean }) => {
          range: (from: number, to: number) => Promise<{ data: unknown; error: unknown }>;
        };
      };
    };
  };
};

export interface LinkRow {
  master_product_id: string;
  identifier_type: string | null;
  identifier_value: string;
  active: boolean | null;
  created_at: string | null;
}

interface CloneOrgRow {
  is_test_clone: boolean | null;
  source_organisation_id: string | null;
  data_cutoff_date: string | null;
  created_at: string | null;
}

const PAGE_SIZE = 1000;

/** The same identity the server merges on: exact-case, Unicode-normalized. */
function linkIdentityKey(row: LinkRow): string {
  return `${toMatchKey(String(row.identifier_type ?? ''))}::${toMatchKey(row.identifier_value)}`;
}

function sourceLinkWithinCutoff(row: LinkRow, cutoffDate: string | null): boolean {
  if (!cutoffDate) return true;
  if (!row.created_at) return true;
  return row.created_at.slice(0, 10) <= cutoffDate;
}

/**
 * Merge a clone's own links with the ones it inherits from its source org.
 *
 * Kept in step with `resolveIdentifierLinksForOrg`
 * (supabase/functions/_shared/variantMapping/linkIdentity.ts), which is what
 * funding resolves through. Without this the review badge read the clone's own
 * rows only, so on a clone it could call a line unmatched that funding had
 * already priced against an inherited link -- the disagreement between badge
 * and figures that FIG-438 rule 6 exists to remove.
 *
 * Source rows come first so a clone's own row for the same identity wins.
 */
export function mergeCloneLinks(
  cloneRows: LinkRow[],
  sourceRows: LinkRow[],
  orgCreatedAt: string | null,
  cutoffDate: string | null,
): LinkRow[] {
  const merged = new Map<string, LinkRow>();
  for (const row of sourceRows) {
    if (!sourceLinkWithinCutoff(row, cutoffDate)) continue;
    merged.set(linkIdentityKey(row), row);
  }
  for (const row of cloneRows) {
    if (!inheritedMappingRowVisible(row.created_at, orgCreatedAt, cutoffDate)) continue;
    merged.set(linkIdentityKey(row), row);
  }
  return [...merged.values()];
}

async function fetchLinks(organisationId: string): Promise<LinkRow[]> {
  const rows: LinkRow[] = [];
  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error } = await supabaseAny
      .from('product_identifier_links')
      .select('master_product_id, identifier_type, identifier_value, active, created_at')
      .eq('organisation_id', organisationId)
      // PostgREST pages are only stable under an explicit order on a unique
      // column; without it rows can be skipped or repeated past one page.
      .order('id', { ascending: true })
      .range(from, from + PAGE_SIZE - 1);
    if (error) throw error;
    const page = (data ?? []) as unknown as LinkRow[];
    rows.push(...page);
    if (page.length < PAGE_SIZE) return rows;
  }
}

async function fetchCloneOrg(organisationId: string): Promise<CloneOrgRow | null> {
  const { data, error } = await supabaseAny
    .from('organisations')
    .select('is_test_clone, source_organisation_id, data_cutoff_date, created_at')
    .eq('id', organisationId)
    .maybeSingle();
  // A clone that cannot be read is treated as a plain org rather than failing
  // the badge outright: the organisation's own links are still the right answer
  // for every non-clone, which is every organisation in production.
  if (error) return null;
  return (data ?? null) as CloneOrgRow | null;
}

/**
 * Every identifier link an organisation can see, inactive ones included, with a
 * clone's inherited links merged in the way the server resolves them.
 *
 * Shared so the review badge and the funding refresh agree about what a
 * Variant holds. Both previously read `product_identifier_links` for the
 * current organisation only, which on a clone is a strictly smaller set than
 * the one funding is computed from.
 */
export async function loadVariantLinks(organisationId: string): Promise<LinkRow[]> {
  const org = await fetchCloneOrg(organisationId);
  const sourceOrganisationId = org?.is_test_clone ? org.source_organisation_id : null;

  if (!sourceOrganisationId) return await fetchLinks(organisationId);

  const [cloneRows, sourceRows] = await Promise.all([
    fetchLinks(organisationId),
    fetchLinks(sourceOrganisationId),
  ]);
  return mergeCloneLinks(
    cloneRows,
    sourceRows,
    org?.created_at ?? null,
    org?.data_cutoff_date ?? null,
  );
}
